/**
 * The file under review. Its name is the switch between the case's files, the line under
 * it says where the mapping stands, and the one action that concerns the whole file —
 * reading it again from the start — sits here rather than inside a table. Pure: state
 * in, actions out.
 */

import type { TFunction } from 'i18next'
import { ChevronsUpDown, ExternalLink, FileSpreadsheet, RefreshCw } from 'lucide-react'
import { type ComponentProps, type FormEvent, type ReactNode, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge, StatusDot } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  analysisProjected,
  type MappingState,
  mappingState,
  type StructuredIngestAnalysis,
  type StructuredIngestAnalysisSummary,
} from '@/services/api/structuredIngest'
import { cn } from '@/utils/cn'
import { formatDateTimeLocalized } from '@/utils/date'
import { useTapTooltip } from './useTapTooltip'

export interface FileMastheadActions {
  onOpenAnalysis: (analysisId: string) => void
  /** The Documents page, or one document on it. */
  onOpenDocuments: (documentId?: string) => void
  /** Read the file again from the start — new tables, new proposals, as a new analysis. */
  onRedoFile: (instructions: string) => Promise<boolean>
}

interface FileMastheadProps {
  analysis: StructuredIngestAnalysis | null
  analyses: StructuredIngestAnalysisSummary[]
  /** A mapping is running for this file — nothing else may start until it finishes. */
  locked: boolean
  actions: FileMastheadActions
}

/** Search only earns its place once the list stops fitting on one screen. */
const SEARCHABLE_FROM = 6

/** How a mapping state shows: the dot on the status line, the badge in the file list. */
const STATUS: Record<
  MappingState,
  {
    dot: 'pending' | 'draft' | 'active' | 'error'
    badge: 'info' | 'warning' | 'success' | 'destructive'
  }
> = {
  preparing: { dot: 'pending', badge: 'info' },
  review: { dot: 'draft', badge: 'warning' },
  projected: { dot: 'active', badge: 'success' },
  failed: { dot: 'error', badge: 'destructive' },
}

const TITLE =
  'flex min-w-0 max-w-full items-center gap-2.5 text-xl font-semibold leading-tight tracking-tight md:text-2xl'

function statusLabel(t: TFunction, state: MappingState): string {
  return t(`tableMapping.file.status.${state}`)
}

export function FileMasthead({ analysis, analyses, locked, actions }: FileMastheadProps) {
  const { t } = useTranslation()
  const addButton = (
    <Button variant="outline" onClick={() => actions.onOpenDocuments()}>
      <FileSpreadsheet aria-hidden />
      {t('tableMapping.picker.addViaDocuments')}
    </Button>
  )
  if (!analysis && analyses.length === 0) return <div className="flex justify-end">{addButton}</div>
  return (
    <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
      <div className="flex min-w-0 flex-1 flex-col gap-1.5 max-lg:basis-full">
        <FileSwitcher
          analysis={analysis}
          analyses={analyses}
          onOpenAnalysis={actions.onOpenAnalysis}
        />
        {analysis && (
          <FileMeta
            analysis={analysis}
            files={analyses.length}
            onOpenDocument={actions.onOpenDocuments}
          />
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 lg:pt-1">
        {analysis?.documentId && (
          <RedoFilePopover analysis={analysis} locked={locked} onRedoFile={actions.onRedoFile} />
        )}
        {addButton}
      </div>
    </header>
  )
}

/** The extension steps back so the name reads first — `xx4` `.csv`. */
function FileName({ filename }: { filename: string }) {
  const dot = filename.lastIndexOf('.')
  const split = dot > 0 && filename.length - dot <= 6
  return (
    <span
      className="truncate max-md:line-clamp-2 max-md:whitespace-normal max-md:break-words"
      title={filename}
    >
      {split ? filename.slice(0, dot) : filename}
      {split && <span className="font-normal text-muted-foreground">{filename.slice(dot)}</span>}
    </span>
  )
}

function FileSwitcher({
  analysis,
  analyses,
  onOpenAnalysis,
}: {
  analysis: StructuredIngestAnalysis | null
  analyses: StructuredIngestAnalysisSummary[]
  onOpenAnalysis: (analysisId: string) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const icon = <FileSpreadsheet className="size-5 shrink-0 text-primary" aria-hidden />
  const title = analysis ? (
    <FileName filename={analysis.filename} />
  ) : (
    <span className="text-muted-foreground">{t('tableMapping.file.pick')}</span>
  )
  if (analysis && analyses.length < 2) {
    return (
      <h2 className={TITLE}>
        {icon}
        {title}
      </h2>
    )
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <h2 className="flex min-w-0 max-w-full">
        <PopoverTrigger asChild>
          <SwitcherButton open={open}>
            {icon}
            {title}
          </SwitcherButton>
        </PopoverTrigger>
      </h2>
      <PopoverContent align="start" className="w-[26rem] p-0">
        <FileList
          analyses={analyses}
          currentId={analysis?.analysisId ?? null}
          onPick={analysisId => {
            onOpenAnalysis(analysisId)
            setOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

/** The heading doubles as the combobox trigger; the chevron says so. */
function SwitcherButton({
  open,
  children,
  ...props
}: { open: boolean; children: ReactNode } & ComponentProps<'button'>) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      role="combobox"
      aria-expanded={open}
      aria-label={t('tableMapping.file.switch')}
      className={cn(
        TITLE,
        'group -ml-2 rounded-xl px-2 py-1 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 aria-expanded:bg-muted'
      )}
      {...props}
    >
      {children}
      <ChevronsUpDown
        className="size-4 shrink-0 text-muted-foreground opacity-60 transition-opacity group-hover:opacity-100 group-aria-expanded:opacity-100"
        aria-hidden
      />
    </button>
  )
}

function FileList({
  analyses,
  currentId,
  onPick,
}: {
  analyses: StructuredIngestAnalysisSummary[]
  currentId: string | null
  onPick: (analysisId: string) => void
}) {
  const { t } = useTranslation()
  const searchable = analyses.length >= SEARCHABLE_FROM
  return (
    <Command>
      {searchable && <CommandInput placeholder={t('tableMapping.file.search')} />}
      <CommandList>
        {searchable && <CommandEmpty>{t('tableMapping.file.noMatch')}</CommandEmpty>}
        <CommandGroup>
          {analyses.map(item => (
            <FileOption
              key={item.analysisId}
              item={item}
              current={item.analysisId === currentId}
              onSelect={() => onPick(item.analysisId)}
            />
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}

function FileOption({
  item,
  current,
  onSelect,
}: {
  item: StructuredIngestAnalysisSummary
  current: boolean
  onSelect: () => void
}) {
  const { t, i18n } = useTranslation()
  const state = mappingState(item)
  return (
    <CommandItem
      value={item.analysisId}
      keywords={[item.filename]}
      data-checked={current}
      onSelect={onSelect}
      className="gap-3 py-2"
    >
      <FileSpreadsheet className="text-muted-foreground" aria-hidden />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium max-md:line-clamp-2 max-md:whitespace-normal max-md:break-all">
          {item.filename}
        </span>
        <span className="text-xs tabular-nums text-muted-foreground">
          {t('tableMapping.file.optionMeta', {
            date: formatDateTimeLocalized(item.createdAt, i18n.language),
            tables: t('tableMapping.count.tables', { count: item.tableCount }),
          })}
        </span>
      </span>
      <Badge variant={STATUS[state].badge}>{statusLabel(t, state)}</Badge>
    </CommandItem>
  )
}

/** Where the file stands, when it was prepared, how big it is: separate facts, no separators. */
function FileMeta({
  analysis,
  files,
  onOpenDocument,
}: {
  analysis: StructuredIngestAnalysis
  files: number
  onOpenDocument: (documentId?: string) => void
}) {
  const { t, i18n } = useTranslation()
  const state = mappingState({ status: analysis.status, projected: analysisProjected(analysis) })
  const rows = analysis.tables.reduce((sum, table) => sum + table.facts.rows, 0)
  const documentId = analysis.documentId
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
      <FileStatus state={state} />
      <span>
        {t('tableMapping.file.prepared', {
          date: formatDateTimeLocalized(analysis.createdAt, i18n.language),
        })}
      </span>
      {analysis.tables.length > 0 && (
        <span className="tabular-nums">
          {t('tableMapping.file.size', {
            tables: t('tableMapping.count.tables', { count: analysis.tables.length }),
            rows: t('tableMapping.count.rows', { count: rows }),
          })}
        </span>
      )}
      {files > 1 && <span>{t('tableMapping.file.inCase', { count: files })}</span>}
      {documentId && (
        <button
          type="button"
          className="inline-flex items-center gap-1 text-primary hover:underline max-md:relative max-md:after:absolute max-md:after:inset-x-0 max-md:after:-inset-y-2.5"
          onClick={() => onOpenDocument(documentId)}
        >
          {t('tableMapping.file.openDocument')}
          <ExternalLink className="size-3" aria-hidden />
        </button>
      )}
    </div>
  )
}

function FileStatus({ state }: { state: MappingState }) {
  const { t } = useTranslation()
  return (
    <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
      <StatusDot
        status={STATUS[state].dot}
        className={cn(state === 'preparing' && 'animate-pulse motion-reduce:animate-none')}
      />
      {statusLabel(t, state)}
    </span>
  )
}

/**
 * A disabled button cannot carry a tooltip itself; the wrapper does — only while blocked.
 * The disabled button lets pointer events through, so on a touch screen a tap on it lands
 * on the wrapper and shows the reason.
 */
function BlockedAction({ reason, children }: { reason: string | null; children: ReactNode }) {
  const tip = useTapTooltip()
  return (
    <Tooltip open={!!reason && tip.open} onOpenChange={tip.onOpenChange}>
      <TooltipTrigger asChild>
        <span className="inline-flex rounded-xl" {...(reason ? tip.trigger : undefined)}>
          {children}
        </span>
      </TooltipTrigger>
      {reason && (
        <TooltipContent className="max-w-xs text-xs leading-snug max-md:mx-2">
          {reason}
        </TooltipContent>
      )}
    </Tooltip>
  )
}

function RedoFilePopover({
  analysis,
  locked,
  onRedoFile,
}: {
  analysis: StructuredIngestAnalysis
  locked: boolean
  onRedoFile: (instructions: string) => Promise<boolean>
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const reason = locked ? t('tableMapping.redo.locked') : null
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <BlockedAction reason={reason}>
        <PopoverTrigger asChild>
          <Button variant="outline" disabled={locked}>
            <RefreshCw aria-hidden />
            {t('tableMapping.redo.button')}
          </Button>
        </PopoverTrigger>
      </BlockedAction>
      <PopoverContent align="end" className="w-[26rem]">
        <RedoFileForm
          analysis={analysis}
          onCancel={() => setOpen(false)}
          onSubmit={async instructions => {
            if (await onRedoFile(instructions)) setOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

function RedoFileIntro({ filename }: { filename: string }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-semibold">{t('tableMapping.redo.title')}</p>
      <p className="text-xs leading-relaxed text-muted-foreground">
        {t('tableMapping.redo.description', { file: filename })}
      </p>
    </div>
  )
}

function InstructionsField({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  const { t } = useTranslation()
  const inputId = useId()
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={inputId}>{t('tableMapping.redo.instructionsLabel')}</Label>
      <Textarea
        id={inputId}
        rows={3}
        maxLength={12000}
        value={value}
        onChange={event => onChange(event.target.value)}
        placeholder={t('tableMapping.redo.instructionsPlaceholder')}
      />
    </div>
  )
}

function RedoFileForm({
  analysis,
  onCancel,
  onSubmit,
}: {
  analysis: StructuredIngestAnalysis
  onCancel: () => void
  onSubmit: (instructions: string) => Promise<void>
}) {
  const { t } = useTranslation()
  const [instructions, setInstructions] = useState(analysis.instructions ?? '')
  const [submitting, setSubmitting] = useState(false)
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (submitting) return
    setSubmitting(true)
    try {
      await onSubmit(instructions.trim())
    } finally {
      setSubmitting(false)
    }
  }
  return (
    <form className="flex flex-col gap-4" onSubmit={submit}>
      <RedoFileIntro filename={analysis.filename} />
      <InstructionsField value={instructions} onChange={setInstructions} />
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('tableMapping.redo.cancel')}
        </Button>
        <Button type="submit" size="sm" loading={submitting}>
          <RefreshCw aria-hidden />
          {t('tableMapping.redo.confirm')}
        </Button>
      </div>
    </form>
  )
}
